import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs/Subject';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/operator/takeUntil';
import { TranslateService } from '@ngx-translate/core';

import {
  SkillAiDateRangeParams,
  SkillAiStatisticsSummaryResponse,
  SkillAiTimeseriesItem,
  SkillAiTopUserItem,
  SkillAiTopSpecializationItem,
  SkillAiLogsResponse,
  SkillAiLogsQueryParams,
  SkillAiBudgetStatusResponse,
  SkillAiLogItem,
  SkillAiOperation,
  StandardAiQuestionGenerationStatusName,
  StandardAiQuestionStatisticsItem,
  StandardAiQuestionStatisticsRequest,
  StandardAiQuestionStatisticsResponse,
  StandardAiQuestionStatisticsSummaryResponse,
  SkillLearningPopularityItemResponse,
  SkillLearningPopularityPageResponse,
  SkillLearningPopularityRequest,
  ContentTranslationCoverageCategory,
  ContentTranslationCoverageResponse,
  ContentTranslationCoverageSectionResponse,
} from '../../../models/skill-learning.model';
import { SkillAiStatisticsFacadeService } from '../../../services/skill-ai-statistics-facade.service';
import { SidebarService } from '../../../../../../services/side-bar.service';
import { ToastService } from '../../../../../../services/toast.service';
import {
  AI_TIMESERIES_METRIC,
  AiTimeseriesMetric,
} from '../ai-statistics-timeseries/ai-statistics-timeseries.component';

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_STANDARD_QUESTION_SORT_BY = 'activityAt';
const TRANSLATION_CATEGORY_KEYS: {
  [category in ContentTranslationCoverageCategory]: string;
} = {
  STANDARDS: 'SKILL_LEARNING_AI_STATS_TRANSLATION_CATEGORY_STANDARDS',
  LEARNING: 'SKILL_LEARNING_AI_STATS_TRANSLATION_CATEGORY_LEARNING',
  SUGGESTIONS: 'SKILL_LEARNING_AI_STATS_TRANSLATION_CATEGORY_SUGGESTIONS',
  KARMA: 'SKILL_LEARNING_AI_STATS_TRANSLATION_CATEGORY_KARMA',
  GENERAL_NOTIFICATIONS: 'SKILL_LEARNING_AI_STATS_TRANSLATION_CATEGORY_GENERAL_NOTIFICATIONS',
};

export const AI_STATISTICS_TAB = {
  LEARNING: 'learning' as 'learning',
  STANDARD_QUESTIONS: 'standardQuestions' as 'standardQuestions',
  LEARNING_POPULARITY: 'learningPopularity' as 'learningPopularity',
  TRANSLATIONS: 'translations' as 'translations',
  PLANFIX_IMPORTS: 'planfixImports' as 'planfixImports',
};

export type AiStatisticsTab = typeof AI_STATISTICS_TAB.LEARNING
  | typeof AI_STATISTICS_TAB.STANDARD_QUESTIONS
  | typeof AI_STATISTICS_TAB.LEARNING_POPULARITY
  | typeof AI_STATISTICS_TAB.TRANSLATIONS
  | typeof AI_STATISTICS_TAB.PLANFIX_IMPORTS;
const DEFAULT_STANDARD_QUESTION_SORT_DIRECTION: 'asc' | 'desc' = 'desc';
const DEFAULT_LEARNING_POPULARITY_SORT_BY = 'startedUsers';
const DEFAULT_LEARNING_POPULARITY_SORT_DIRECTION: 'asc' | 'desc' = 'desc';
const DEFAULT_TOP_LIMIT = 10;
const DEFAULT_RANGE_DAYS = 6;
const START_OF_DAY_TIME = '00:00:00';
const END_OF_DAY_TIME = '23:59:59';

interface StandardQuestionStatisticsViewItem extends StandardAiQuestionStatisticsItem {
  languageKey: string;
  statusKey: string;
  statusClass: string;
}

interface StandardQuestionStatisticsViewResponse {
  summary: StandardAiQuestionStatisticsSummaryResponse;
  items: StandardQuestionStatisticsViewItem[];
  page: number;
  size: number;
  totalItems: number;
  totalPages: number;
  sortBy: string;
  sortDirection: 'asc' | 'desc';
}

@Component({
  selector: 'app-ai-statistics-container',
  templateUrl: './ai-statistics-container.component.html',
  styleUrls: ['./ai-statistics-container.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiStatisticsContainerComponent implements OnInit, OnDestroy {

  readonly tabs = AI_STATISTICS_TAB;

  activeTab: AiStatisticsTab = AI_STATISTICS_TAB.LEARNING;
  summary: SkillAiStatisticsSummaryResponse = this.createEmptySummary();
  timeseries: SkillAiTimeseriesItem[] = [];
  topUsers: SkillAiTopUserItem[] = [];
  topSpecializations: SkillAiTopSpecializationItem[] = [];
  budgetStatus: SkillAiBudgetStatusResponse = this.createEmptyBudgetStatus();
  logs: SkillAiLogItem[] = [];
  standardQuestionStatistics: StandardQuestionStatisticsViewResponse = this.createEmptyStandardQuestionStatistics();
  learningPopularityStatistics: SkillLearningPopularityPageResponse = this.createEmptyLearningPopularityStatistics();
  translationCoverage: ContentTranslationCoverageResponse = this.createEmptyTranslationCoverage();
  selectedMetric: AiTimeseriesMetric = AI_TIMESERIES_METRIC.REQUESTS;
  isOverviewLoading = true;
  isLogsLoading = true;
  isStandardQuestionStatisticsLoading = false;
  isLearningPopularityLoading = false;
  isTranslationCoverageLoading = false;
  hasOverviewError = false;
  hasLogsError = false;
  hasStandardQuestionStatisticsError = false;
  hasLearningPopularityError = false;
  hasTranslationCoverageError = false;
  standardQuestionSortIndicators: { [key: string]: string } = {};
  standardQuestionSortAriaStates: { [key: string]: string } = {};
  learningPopularitySortIndicators: { [key: string]: string } = {};
  learningPopularitySortAriaStates: { [key: string]: string } = {};
  page = 1;
  totalLogItems = 0;
  standardQuestionPage = 1;
  standardQuestionTotalItems = 0;
  learningPopularityPage = 1;
  learningPopularityTotalItems = 0;
  standardQuestionSortBy = DEFAULT_STANDARD_QUESTION_SORT_BY;
  standardQuestionSortDirection: 'asc' | 'desc' = DEFAULT_STANDARD_QUESTION_SORT_DIRECTION;
  learningPopularitySortBy = DEFAULT_LEARNING_POPULARITY_SORT_BY;
  learningPopularitySortDirection: 'asc' | 'desc' = DEFAULT_LEARNING_POPULARITY_SORT_DIRECTION;
  readonly pageSize: number = DEFAULT_PAGE_SIZE;
  readonly standardQuestionPageSize: number = DEFAULT_PAGE_SIZE;
  readonly learningPopularityPageSize: number = DEFAULT_PAGE_SIZE;
  dateFrom: any;
  dateTo: any;

  readonly metricOptions = [
    { value: AI_TIMESERIES_METRIC.REQUESTS, key: 'SKILL_LEARNING_AI_STATS_CHART_METRIC_REQUESTS' },
    { value: AI_TIMESERIES_METRIC.TOKENS, key: 'SKILL_LEARNING_AI_STATS_CHART_METRIC_TOKENS' },
    { value: AI_TIMESERIES_METRIC.COST, key: 'SKILL_LEARNING_AI_STATS_CHART_METRIC_COST' },
  ];

  readonly operationOptions = [
    { value: '', key: 'SKILL_LEARNING_AI_STATS_FILTER_ALL' },
    { value: 'CHAT', key: 'SKILL_LEARNING_AI_STATS_OPERATION_CHAT' },
    { value: 'TEST_GENERATION', key: 'SKILL_LEARNING_AI_STATS_OPERATION_TEST_GENERATION' },
    { value: 'STANDARD_TEST_GENERATION', key: 'SKILL_LEARNING_AI_STATS_OPERATION_STANDARD_TEST_GENERATION' },
  ];

  readonly successOptions = [
    { value: '', key: 'SKILL_LEARNING_AI_STATS_FILTER_ALL' },
    { value: 'true', key: 'SKILL_LEARNING_AI_STATS_FILTER_SUCCESS' },
    { value: 'false', key: 'SKILL_LEARNING_AI_STATS_FILTER_FAILED' },
  ];

  readonly standardQuestionSortOptions = [
    { value: 'activityAt', key: 'SKILL_LEARNING_AI_STATS_STANDARD_SORT_ACTIVITY_AT' },
    { value: 'updatedAt', key: 'SKILL_LEARNING_AI_STATS_STANDARD_COLUMN_UPDATED_AT' },
    { value: 'generatedAt', key: 'SKILL_LEARNING_AI_STATS_STANDARD_COLUMN_GENERATED_AT' },
    { value: 'createdAt', key: 'SKILL_LEARNING_AI_STATS_COLUMN_CREATED_AT' },
    { value: 'nextAttemptAt', key: 'SKILL_LEARNING_AI_STATS_STANDARD_SORT_NEXT_ATTEMPT_AT' },
    { value: 'standardNumber', key: 'SKILL_LEARNING_AI_STATS_STANDARD_SORT_STANDARD_NUMBER' },
    { value: 'standardName', key: 'SKILL_LEARNING_AI_STATS_STANDARD_SORT_STANDARD_NAME' },
    { value: 'language', key: 'SKILL_LEARNING_AI_STATS_STANDARD_COLUMN_LANGUAGE' },
    { value: 'status', key: 'SKILL_LEARNING_AI_STATS_COLUMN_STATUS' },
    { value: 'success', key: 'SKILL_LEARNING_AI_STATS_COLUMN_SUCCESS' },
    { value: 'generatedQuestionCount', key: 'SKILL_LEARNING_AI_STATS_COLUMN_ACTUAL_QUESTIONS' },
    { value: 'errorCode', key: 'SKILL_LEARNING_AI_STATS_COLUMN_ERROR_CODE' },
    { value: 'lastTriggerReason', key: 'SKILL_LEARNING_AI_STATS_STANDARD_COLUMN_TRIGGER' },
    { value: 'provider', key: 'SKILL_LEARNING_AI_STATS_COLUMN_PROVIDER' },
    { value: 'model', key: 'SKILL_LEARNING_AI_STATS_COLUMN_MODEL' },
    { value: 'attempts', key: 'SKILL_LEARNING_AI_STATS_STANDARD_COLUMN_ATTEMPTS' },
    { value: 'maxAttempts', key: 'SKILL_LEARNING_AI_STATS_STANDARD_SORT_MAX_ATTEMPTS' },
    { value: 'sourceIdentity', key: 'SKILL_LEARNING_AI_STATS_STANDARD_COLUMN_SOURCE' },
    { value: 'stateId', key: 'SKILL_LEARNING_AI_STATS_STANDARD_SORT_STATE_ID' },
    { value: 'standardId', key: 'SKILL_LEARNING_AI_STATS_STANDARD_SORT_STANDARD_ID' },
  ];

  readonly standardQuestionSortDirectionOptions = [
    { value: 'desc', key: 'SKILL_LEARNING_AI_STATS_STANDARD_SORT_DESC' },
    { value: 'asc', key: 'SKILL_LEARNING_AI_STATS_STANDARD_SORT_ASC' },
  ];

  readonly learningPopularitySortOptions = [
    { value: 'startedUsers', key: 'SKILL_LEARNING_STATS_POPULARITY_COLUMN_STARTED' },
    { value: 'level1SelfTestPassedUsers', key: 'SKILL_LEARNING_STATS_POPULARITY_COLUMN_LEVEL1_SELF' },
    { value: 'level1TestReviewPassedUsers', key: 'SKILL_LEARNING_STATS_POPULARITY_COLUMN_LEVEL1_REVIEW' },
    { value: 'level2PassedUsers', key: 'SKILL_LEARNING_STATS_POPULARITY_COLUMN_LEVEL2' },
    { value: 'level3PassedUsers', key: 'SKILL_LEARNING_STATS_POPULARITY_COLUMN_LEVEL3' },
    { value: 'completedUsers', key: 'SKILL_LEARNING_STATS_POPULARITY_COLUMN_COMPLETED' },
    { value: 'completionPercent', key: 'SKILL_LEARNING_STATS_POPULARITY_COLUMN_COMPLETION_PERCENT' },
    { value: 'specializationName', key: 'SKILL_LEARNING_STATS_POPULARITY_COLUMN_SPECIALIZATION' },
    { value: 'groupName', key: 'SKILL_LEARNING_STATS_POPULARITY_COLUMN_GROUP' },
  ];

  readonly rangeForm = new FormGroup({
    from: new FormControl(),
    to: new FormControl(),
  });

  readonly logsForm = new FormGroup({
    operation: new FormControl(''),
    success: new FormControl(''),
    model: new FormControl(''),
    userId: new FormControl(''),
    specializationId: new FormControl(''),
  });

  private destroy$ = new Subject<void>();

  constructor(
    private facade: SkillAiStatisticsFacadeService,
    private sidebarService: SidebarService,
    private toastService: ToastService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.sidebarService.setSelectedMenuOption('ai-statistics');
    this.setDefaultRange();
    this.rebuildStandardQuestionSortState();
    this.rebuildLearningPopularitySortState();
    this.loadPageData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get minToDate(): NgbDateStruct | null {
    return this.rangeForm.controls['from'].value || null;
  }

  get maxFromDate(): NgbDateStruct | null {
    return this.rangeForm.controls['to'].value || null;
  }

  get canApplyRange(): boolean {
    const range = this.buildDateRangeParams();
    return !!range;
  }

  onApplyRange(): void {
    if (!this.canApplyRange) {
      return;
    }
    this.loadPageData();
  }

  onTabSelected(tab: AiStatisticsTab): void {
    this.activeTab = tab;
    if (tab === AI_STATISTICS_TAB.STANDARD_QUESTIONS) {
      this.loadStandardQuestionStatistics();
    }
    if (tab === AI_STATISTICS_TAB.LEARNING_POPULARITY) {
      this.loadLearningPopularityStatistics();
    }
    if (tab === AI_STATISTICS_TAB.TRANSLATIONS) {
      this.loadTranslationCoverageStatistics();
    }
    this.cdr.markForCheck();
  }

  onRefreshStandardQuestionStatistics(): void {
    this.loadStandardQuestionStatistics();
  }

  onStandardQuestionPageChanged(page: number): void {
    this.loadStandardQuestionStatistics(page);
  }

  onStandardQuestionSortByChanged(sortBy: string): void {
    if (!sortBy || this.standardQuestionSortBy === sortBy) {
      return;
    }
    this.standardQuestionSortBy = sortBy;
    this.standardQuestionSortDirection = this.defaultStandardQuestionSortDirection(sortBy);
    this.rebuildStandardQuestionSortState();
    this.loadStandardQuestionStatistics(1);
  }

  onStandardQuestionSortDirectionChanged(direction: string): void {
    if (direction !== 'asc' && direction !== 'desc') {
      return;
    }
    if (this.standardQuestionSortDirection === direction) {
      return;
    }
    this.standardQuestionSortDirection = direction;
    this.rebuildStandardQuestionSortState();
    this.loadStandardQuestionStatistics(1);
  }

  onStandardQuestionColumnSort(sortBy: string): void {
    if (this.standardQuestionSortBy === sortBy) {
      this.standardQuestionSortDirection = this.standardQuestionSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.standardQuestionSortBy = sortBy;
      this.standardQuestionSortDirection = this.defaultStandardQuestionSortDirection(sortBy);
    }
    this.rebuildStandardQuestionSortState();
    this.loadStandardQuestionStatistics(1);
  }

  onRefreshLearningPopularityStatistics(): void {
    this.loadLearningPopularityStatistics();
  }

  onRefreshTranslationCoverageStatistics(): void {
    this.loadTranslationCoverageStatistics();
  }

  onLearningPopularityPageChanged(page: number): void {
    this.loadLearningPopularityStatistics(page);
  }

  onLearningPopularitySortByChanged(sortBy: string): void {
    if (!sortBy || this.learningPopularitySortBy === sortBy) {
      return;
    }
    this.learningPopularitySortBy = sortBy;
    this.learningPopularitySortDirection = this.defaultLearningPopularitySortDirection(sortBy);
    this.rebuildLearningPopularitySortState();
    this.loadLearningPopularityStatistics(1);
  }

  onLearningPopularitySortDirectionChanged(direction: string): void {
    if (direction !== 'asc' && direction !== 'desc') {
      return;
    }
    if (this.learningPopularitySortDirection === direction) {
      return;
    }
    this.learningPopularitySortDirection = direction;
    this.rebuildLearningPopularitySortState();
    this.loadLearningPopularityStatistics(1);
  }

  onLearningPopularityColumnSort(sortBy: string): void {
    if (this.learningPopularitySortBy === sortBy) {
      this.learningPopularitySortDirection = this.learningPopularitySortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.learningPopularitySortBy = sortBy;
      this.learningPopularitySortDirection = this.defaultLearningPopularitySortDirection(sortBy);
    }
    this.rebuildLearningPopularitySortState();
    this.loadLearningPopularityStatistics(1);
  }

  onResetLogsFilters(): void {
    this.logsForm.reset({
      operation: '',
      success: '',
      model: '',
      userId: '',
      specializationId: '',
    });
    this.loadLogsFirstPage();
  }

  onApplyLogsFilters(): void {
    this.loadLogsFirstPage();
  }

  onTimeseriesMetricSelected(metric: AiTimeseriesMetric): void {
    this.selectedMetric = metric;
    this.cdr.markForCheck();
  }

  onLogsPageChanged(page: number): void {
    this.loadLogs(page);
  }

  trackByUserId(index: number, item: SkillAiTopUserItem): string {
    return item ? item.userId : String(index);
  }

  trackBySpecializationId(index: number, item: SkillAiTopSpecializationItem): string {
    return item ? item.specializationId : String(index);
  }

  trackByLogItem(index: number, item: SkillAiLogItem): string {
    return item && item.id ? item.id : String(index);
  }

  trackByStandardQuestionState(index: number, item: StandardQuestionStatisticsViewItem): string {
    return item && item.stateId ? item.stateId : String(index);
  }

  trackByLearningPopularityItem(index: number, item: SkillLearningPopularityItemResponse): string {
    return item && item.specializationId ? item.specializationId : String(index);
  }

  trackByTranslationCoverageCategory(index: number, item: ContentTranslationCoverageSectionResponse): string {
    return item && item.category ? item.category : String(index);
  }

  getTranslationCategoryKey(category: ContentTranslationCoverageCategory): string {
    return TRANSLATION_CATEGORY_KEYS[category];
  }

  getOperationKey(value: SkillAiOperation): string {
    if (value === 'STANDARD_TEST_GENERATION') {
      return 'SKILL_LEARNING_AI_STATS_OPERATION_STANDARD_TEST_GENERATION';
    }
    if (value === 'TEST_GENERATION') {
      return 'SKILL_LEARNING_AI_STATS_OPERATION_TEST_GENERATION';
    }
    return 'SKILL_LEARNING_AI_STATS_OPERATION_CHAT';
  }

  getSuccessKey(success: boolean): string {
    if (success) {
      return 'SKILL_LEARNING_AI_STATS_FILTER_SUCCESS';
    }
    return 'SKILL_LEARNING_AI_STATS_FILTER_FAILED';
  }

  private loadPageData(): void {
    this.loadOverview();
    this.loadLogsFirstPage();
  }

  private loadOverview(): void {
    const range = this.buildDateRangeParams();
    if (!range) {
      return;
    }
    this.isOverviewLoading = true;
    this.hasOverviewError = false;
    this.cdr.markForCheck();
    Observable.forkJoin(
      this.facade.fetchSummary(range),
      this.facade.fetchTimeseries(range),
      this.facade.fetchTopUsers(range, DEFAULT_TOP_LIMIT),
      this.facade.fetchTopSpecializations(range, DEFAULT_TOP_LIMIT),
      this.facade.fetchBudgetStatus(),
    )
      .takeUntil(this.destroy$)
      .subscribe(
        result => this.handleOverviewLoadSuccess(result),
        () => this.handleOverviewLoadError(),
      );
  }

  private loadLogsFirstPage(): void {
    this.loadLogs(1);
  }

  private loadLogs(page: number): void {
    const params = this.buildLogsQuery(page);
    if (!params) {
      return;
    }
    this.isLogsLoading = true;
    this.hasLogsError = false;
    this.cdr.markForCheck();
    this.facade.fetchLogs(params)
      .takeUntil(this.destroy$)
      .subscribe(
        response => this.handleLogsLoadSuccess(response),
        () => this.handleLogsLoadError(),
      );
  }

  private loadStandardQuestionStatistics(page: number = this.standardQuestionPage): void {
    this.isStandardQuestionStatisticsLoading = true;
    this.hasStandardQuestionStatisticsError = false;
    this.cdr.markForCheck();
    this.facade.fetchStandardQuestionStatistics(this.buildStandardQuestionQuery(page))
      .takeUntil(this.destroy$)
      .subscribe(
        response => this.handleStandardQuestionStatisticsSuccess(response),
        () => this.handleStandardQuestionStatisticsError(),
      );
  }

  private loadLearningPopularityStatistics(page: number = this.learningPopularityPage): void {
    this.isLearningPopularityLoading = true;
    this.hasLearningPopularityError = false;
    this.cdr.markForCheck();
    this.facade.fetchLearningPopularityStatistics(this.buildLearningPopularityQuery(page))
      .takeUntil(this.destroy$)
      .subscribe(
        response => this.handleLearningPopularitySuccess(response),
        () => this.handleLearningPopularityError(),
      );
  }

  private loadTranslationCoverageStatistics(): void {
    this.isTranslationCoverageLoading = true;
    this.hasTranslationCoverageError = false;
    this.cdr.markForCheck();
    this.facade.fetchTranslationCoverageStatistics()
      .takeUntil(this.destroy$)
      .subscribe(
        response => this.handleTranslationCoverageSuccess(response),
        () => this.handleTranslationCoverageError(),
      );
  }

  private handleOverviewLoadSuccess(result: [
    SkillAiStatisticsSummaryResponse,
    SkillAiTimeseriesItem[],
    SkillAiTopUserItem[],
    SkillAiTopSpecializationItem[],
    SkillAiBudgetStatusResponse
  ]): void {
    this.summary = result[0];
    this.timeseries = result[1];
    this.topUsers = result[2];
    this.topSpecializations = result[3];
    this.budgetStatus = result[4];
    this.isOverviewLoading = false;
    this.hasOverviewError = false;
    this.cdr.markForCheck();
  }

  private handleOverviewLoadError(): void {
    this.isOverviewLoading = false;
    this.hasOverviewError = true;
    this.toastService.error(this.translate.instant('SKILL_LEARNING_AI_STATS_OVERVIEW_ERROR'));
    this.cdr.markForCheck();
  }

  private handleLogsLoadSuccess(response: SkillAiLogsResponse): void {
    this.logs = response.items || [];
    this.page = response.page + 1;
    this.totalLogItems = response.totalItems || 0;
    this.isLogsLoading = false;
    this.hasLogsError = false;
    this.cdr.markForCheck();
  }

  private handleLogsLoadError(): void {
    this.isLogsLoading = false;
    this.hasLogsError = true;
    this.toastService.error(this.translate.instant('SKILL_LEARNING_AI_STATS_LOGS_ERROR'));
    this.cdr.markForCheck();
  }

  private handleStandardQuestionStatisticsSuccess(response: StandardAiQuestionStatisticsResponse): void {
    this.standardQuestionStatistics = this.createStandardQuestionViewStatistics(response);
    this.standardQuestionPage = (this.standardQuestionStatistics.page || 0) + 1;
    this.standardQuestionTotalItems = this.standardQuestionStatistics.totalItems || this.standardQuestionStatistics.items.length;
    this.standardQuestionSortBy = this.standardQuestionStatistics.sortBy || this.standardQuestionSortBy;
    this.standardQuestionSortDirection = this.standardQuestionStatistics.sortDirection || this.standardQuestionSortDirection;
    this.rebuildStandardQuestionSortState();
    this.isStandardQuestionStatisticsLoading = false;
    this.hasStandardQuestionStatisticsError = false;
    this.cdr.markForCheck();
  }

  private handleStandardQuestionStatisticsError(): void {
    this.isStandardQuestionStatisticsLoading = false;
    this.hasStandardQuestionStatisticsError = true;
    this.toastService.error(this.translate.instant('SKILL_LEARNING_AI_STATS_STANDARD_ERROR'));
    this.cdr.markForCheck();
  }

  private handleLearningPopularitySuccess(response: SkillLearningPopularityPageResponse): void {
    this.learningPopularityStatistics = this.createLearningPopularityStatistics(response);
    this.learningPopularityPage = (this.learningPopularityStatistics.page || 0) + 1;
    this.learningPopularityTotalItems = this.learningPopularityStatistics.totalItems || this.learningPopularityStatistics.items.length;
    this.learningPopularitySortBy = this.learningPopularityStatistics.sortBy || this.learningPopularitySortBy;
    this.learningPopularitySortDirection = this.learningPopularityStatistics.sortDirection || this.learningPopularitySortDirection;
    this.rebuildLearningPopularitySortState();
    this.isLearningPopularityLoading = false;
    this.hasLearningPopularityError = false;
    this.cdr.markForCheck();
  }

  private handleLearningPopularityError(): void {
    this.isLearningPopularityLoading = false;
    this.hasLearningPopularityError = true;
    this.toastService.error(this.translate.instant('SKILL_LEARNING_STATS_POPULARITY_ERROR'));
    this.cdr.markForCheck();
  }

  private handleTranslationCoverageSuccess(response: ContentTranslationCoverageResponse): void {
    this.translationCoverage = this.createTranslationCoverage(response);
    this.isTranslationCoverageLoading = false;
    this.hasTranslationCoverageError = false;
    this.cdr.markForCheck();
  }

  private handleTranslationCoverageError(): void {
    this.isTranslationCoverageLoading = false;
    this.hasTranslationCoverageError = true;
    this.toastService.error(this.translate.instant('SKILL_LEARNING_AI_STATS_TRANSLATION_ERROR'));
    this.cdr.markForCheck();
  }

  private buildLogsQuery(page: number): SkillAiLogsQueryParams | null {
    const range = this.buildDateRangeParams();
    if (!range) {
      return null;
    }
    return {
      from: range.from,
      to: range.to,
      page: page - 1,
      size: this.pageSize,
      operation: this.resolveOperationFilter(),
      success: this.resolveSuccessFilter(),
      model: this.resolveTextControlValue('model'),
      userId: this.resolveTextControlValue('userId'),
      specializationId: this.resolveTextControlValue('specializationId'),
    };
  }

  private buildStandardQuestionQuery(page: number): StandardAiQuestionStatisticsRequest {
    return {
      page: Math.max(0, page - 1),
      size: this.standardQuestionPageSize,
      sortBy: this.standardQuestionSortBy,
      sortDirection: this.standardQuestionSortDirection,
    };
  }

  private buildLearningPopularityQuery(page: number): SkillLearningPopularityRequest {
    return {
      page: Math.max(0, page - 1),
      size: this.learningPopularityPageSize,
      sortBy: this.learningPopularitySortBy,
      sortDirection: this.learningPopularitySortDirection,
    };
  }

  private defaultStandardQuestionSortDirection(sortBy: string): 'asc' | 'desc' {
    if (sortBy === 'standardNumber' || sortBy === 'standardName' || sortBy === 'language' || sortBy === 'status') {
      return 'asc';
    }
    return 'desc';
  }

  private defaultLearningPopularitySortDirection(sortBy: string): 'asc' | 'desc' {
    if (sortBy === 'specializationName' || sortBy === 'groupName') {
      return 'asc';
    }
    return 'desc';
  }

  private rebuildStandardQuestionSortState(): void {
    const indicators: { [key: string]: string } = {};
    const ariaStates: { [key: string]: string } = {};
    this.standardQuestionSortOptions.forEach(option => {
      indicators[option.value] = this.resolveStandardQuestionSortIndicator(option.value);
      ariaStates[option.value] = this.resolveStandardQuestionAriaSortState(option.value);
    });
    this.standardQuestionSortIndicators = indicators;
    this.standardQuestionSortAriaStates = ariaStates;
  }

  private rebuildLearningPopularitySortState(): void {
    const indicators: { [key: string]: string } = {};
    const ariaStates: { [key: string]: string } = {};
    this.learningPopularitySortOptions.forEach(option => {
      indicators[option.value] = this.resolveLearningPopularitySortIndicator(option.value);
      ariaStates[option.value] = this.resolveLearningPopularityAriaSortState(option.value);
    });
    this.learningPopularitySortIndicators = indicators;
    this.learningPopularitySortAriaStates = ariaStates;
  }

  private resolveStandardQuestionSortIndicator(sortBy: string): string {
    if (this.standardQuestionSortBy !== sortBy) {
      return '';
    }
    return this.standardQuestionSortDirection === 'asc' ? '^' : 'v';
  }

  private resolveStandardQuestionAriaSortState(sortBy: string): string {
    if (this.standardQuestionSortBy !== sortBy) {
      return 'none';
    }
    return this.standardQuestionSortDirection === 'asc' ? 'ascending' : 'descending';
  }

  private resolveLearningPopularitySortIndicator(sortBy: string): string {
    if (this.learningPopularitySortBy !== sortBy) {
      return '';
    }
    return this.learningPopularitySortDirection === 'asc' ? '^' : 'v';
  }

  private resolveLearningPopularityAriaSortState(sortBy: string): string {
    if (this.learningPopularitySortBy !== sortBy) {
      return 'none';
    }
    return this.learningPopularitySortDirection === 'asc' ? 'ascending' : 'descending';
  }

  private buildDateRangeParams(): SkillAiDateRangeParams | null {
    const from = this.rangeForm.controls['from'].value;
    const to = this.rangeForm.controls['to'].value;
    if (!this.isValidRange(from, to)) {
      return null;
    }
    return {
      from: this.formatFromDateValue(from),
      to: this.formatToDateValue(to),
    };
  }

  private resolveOperationFilter(): SkillAiOperation | null {
    const value = this.logsForm.controls['operation'].value;
    if (value === 'CHAT' || value === 'TEST_GENERATION' || value === 'STANDARD_TEST_GENERATION') {
      return value as SkillAiOperation;
    }
    return null;
  }

  private resolveSuccessFilter(): boolean | null {
    const value = this.logsForm.controls['success'].value;
    if (value === 'true') {
      return true;
    }
    if (value === 'false') {
      return false;
    }
    return null;
  }

  private resolveTextControlValue(controlName: string): string {
    const value = this.logsForm.controls[controlName].value;
    return value ? String(value).trim() : '';
  }

  private setDefaultRange(): void {
    const toDate = new Date();
    const fromDate = this.subtractDays(toDate, DEFAULT_RANGE_DAYS);
    this.rangeForm.setValue({
      from: this.toDateStruct(fromDate),
      to: this.toDateStruct(toDate),
    });
  }

  private subtractDays(source: Date, days: number): Date {
    const result = new Date(source.getTime());
    result.setDate(result.getDate() - days);
    return result;
  }

  private toDateStruct(date: Date): NgbDateStruct {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    };
  }

  private isValidRange(from: NgbDateStruct | null, to: NgbDateStruct | null): boolean {
    if (!this.isDateStruct(from) || !this.isDateStruct(to)) {
      return false;
    }
    return this.compareDates(from, to) <= 0;
  }

  private isDateStruct(value: NgbDateStruct | null): boolean {
    if (!value) {
      return false;
    }
    return !!value.year && !!value.month && !!value.day;
  }

  private compareDates(left: NgbDateStruct, right: NgbDateStruct): number {
    const leftValue = `${left.year}${this.pad(left.month)}${this.pad(left.day)}`;
    const rightValue = `${right.year}${this.pad(right.month)}${this.pad(right.day)}`;
    return leftValue.localeCompare(rightValue);
  }

  private formatFromDateValue(value: NgbDateStruct): string {
    return `${this.formatDatePart(value)}T${START_OF_DAY_TIME}`;
  }

  private formatToDateValue(value: NgbDateStruct): string {
    return `${this.formatDatePart(value)}T${END_OF_DAY_TIME}`;
  }

  private formatDatePart(value: NgbDateStruct): string {
    return `${value.year}-${this.pad(value.month)}-${this.pad(value.day)}`;
  }

  private pad(value: number): string {
    return value < 10 ? `0${value}` : String(value);
  }

  private createEmptySummary(): SkillAiStatisticsSummaryResponse {
    return {
      totalRequests: 0,
      successfulRequests: 0,
      failedRequests: 0,
      totalTokens: 0,
      totalInputTokens: 0,
      totalOutputTokens: 0,
      estimatedCost: 0,
      averageLatencyMs: 0,
      successRate: 0,
      operationBreakdown: [],
      modelBreakdown: [],
    };
  }

  private createEmptyBudgetStatus(): SkillAiBudgetStatusResponse {
    return {
      globalTokensUsed: 0,
      globalTokenLimit: 0,
      usagePercentage: 0,
      status: 'normal',
      testGenerationRequestsToday: 0,
      topUsers: [],
    };
  }

  private createEmptyStandardQuestionStatistics(): StandardQuestionStatisticsViewResponse {
    return {
      summary: this.createEmptyStandardQuestionSummary(),
      items: [],
      page: 0,
      size: DEFAULT_PAGE_SIZE,
      totalItems: 0,
      totalPages: 0,
      sortBy: this.standardQuestionSortBy || DEFAULT_STANDARD_QUESTION_SORT_BY,
      sortDirection: this.standardQuestionSortDirection || DEFAULT_STANDARD_QUESTION_SORT_DIRECTION,
    };
  }

  private createEmptyLearningPopularityStatistics(): SkillLearningPopularityPageResponse {
    return {
      items: [],
      page: 0,
      size: DEFAULT_PAGE_SIZE,
      totalItems: 0,
      totalPages: 0,
      sortBy: this.learningPopularitySortBy || DEFAULT_LEARNING_POPULARITY_SORT_BY,
      sortDirection: this.learningPopularitySortDirection || DEFAULT_LEARNING_POPULARITY_SORT_DIRECTION,
    };
  }

  private createEmptyTranslationCoverage(): ContentTranslationCoverageResponse {
    return {
      generatingNow: false,
      calculatedAt: null,
      sections: [],
    };
  }

  private createTranslationCoverage(
    response: ContentTranslationCoverageResponse | null,
  ): ContentTranslationCoverageResponse {
    const coverage = response || this.createEmptyTranslationCoverage();
    return {
      generatingNow: !!coverage.generatingNow,
      calculatedAt: coverage.calculatedAt || null,
      sections: coverage.sections || [],
    };
  }

  private createLearningPopularityStatistics(
    response: SkillLearningPopularityPageResponse | null,
  ): SkillLearningPopularityPageResponse {
    const statistics = response || this.createEmptyLearningPopularityStatistics();
    return {
      items: statistics.items || [],
      page: statistics.page || 0,
      size: statistics.size || DEFAULT_PAGE_SIZE,
      totalItems: statistics.totalItems || 0,
      totalPages: statistics.totalPages || 0,
      sortBy: statistics.sortBy || DEFAULT_LEARNING_POPULARITY_SORT_BY,
      sortDirection: statistics.sortDirection || DEFAULT_LEARNING_POPULARITY_SORT_DIRECTION,
    };
  }

  private createEmptyStandardQuestionSummary(): StandardAiQuestionStatisticsSummaryResponse {
    return {
      totalStandards: 0,
      trackedStandards: 0,
      generatedStandards: 0,
      failedStandards: 0,
      pendingStandards: 0,
      processingStandards: 0,
      totalGenerationStates: 0,
      activeTrackedStandards: 0,
      draftGenerationStates: 0,
      orphanedGenerationStates: 0,
      pendingGenerations: 0,
      processingGenerations: 0,
      successfulGenerations: 0,
      failedGenerations: 0,
      skippedGenerations: 0,
      manualOverrideGenerations: 0,
      totalGeneratedQuestions: 0,
    };
  }

  private createStandardQuestionViewStatistics(
    response: StandardAiQuestionStatisticsResponse | null
  ): StandardQuestionStatisticsViewResponse {
    const statistics = response || this.createEmptyStandardQuestionStatistics();
    return {
      summary: statistics.summary || this.createEmptyStandardQuestionSummary(),
      items: this.createStandardQuestionViewItems(statistics.items || []),
      page: statistics.page || 0,
      size: statistics.size || DEFAULT_PAGE_SIZE,
      totalItems: statistics.totalItems || 0,
      totalPages: statistics.totalPages || 0,
      sortBy: statistics.sortBy || DEFAULT_STANDARD_QUESTION_SORT_BY,
      sortDirection: statistics.sortDirection || DEFAULT_STANDARD_QUESTION_SORT_DIRECTION,
    };
  }

  private createStandardQuestionViewItems(
    items: StandardAiQuestionStatisticsItem[]
  ): StandardQuestionStatisticsViewItem[] {
    return items.map(item => this.createStandardQuestionViewItem(item));
  }

  private createStandardQuestionViewItem(
    item: StandardAiQuestionStatisticsItem
  ): StandardQuestionStatisticsViewItem {
    return Object.assign({}, item, {
      languageKey: this.resolveStandardQuestionLanguageKey(item.language),
      statusKey: this.resolveStandardQuestionStatusKey(item),
      statusClass: this.resolveStandardQuestionStatusClass(item.status),
    });
  }

  private resolveStandardQuestionStatusKey(item: StandardAiQuestionStatisticsItem): string {
    if (item && item.status === 'MANUAL_OVERRIDE' && item.generatedAt) {
      return 'SKILL_LEARNING_AI_STATS_STANDARD_STATUS_MANUAL_OVERRIDE_AFTER_AI';
    }
    return `SKILL_LEARNING_AI_STATS_STANDARD_STATUS_${item.status}`;
  }

  private resolveStandardQuestionStatusClass(status: StandardAiQuestionGenerationStatusName): string {
    if (status === 'SUCCEEDED') {
      return 'ai-stats-page__status-pill--success';
    }
    if (status === 'FAILED') {
      return 'ai-stats-page__status-pill--failed';
    }
    if (status === 'PENDING' || status === 'PROCESSING') {
      return 'ai-stats-page__status-pill--pending';
    }
    return 'ai-stats-page__status-pill--muted';
  }

  private resolveStandardQuestionLanguageKey(language: string): string {
    if (language === 'RUSSIAN') {
      return 'SKILL_LEARNING_AI_STATS_STANDARD_LANGUAGE_RUSSIAN';
    }
    if (language === 'ENGLISH') {
      return 'SKILL_LEARNING_AI_STATS_STANDARD_LANGUAGE_ENGLISH';
    }
    return '';
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/components/admin/ai-statistics-container/ai-statistics-container.component.ts